// Copyright (C) 2012 Minted Inc.
// All Rights Reserved
M = M || {};
M.Model = M.Model || {};
M.Model.ProductDetails = M.Model.ProductDetails || {};

// TODO: Rename ProductOptionsPanel and ProductOptionsPricing views and models.
// Their names do not adequately reflect their functionality.

/**
 * This model represents the product options selected for a product and
 * calculates the pricing for those options.
 *
 * It requires a JSON representation of a minted_product and pricing map
 * that gives the product pricing for each of the permutations of
 * format, paper type, and quantity available for the product.
 *
 * TODO: ProductOptionsPricing and ComputedPrice share much of the same logic,
 * but compute pricing differently. We should consolidate the two models.
 */
M.Model.ProductDetails.ProductOptionsPricing = Backbone.Model.extend({

  /**
   * Initialize the model. priceMap and productData are required
   * attributes.
   *
   * @param {dict} productData A JSON representation of a minted_product.
   * @param {dict} priceMap A map of format->paper type->quantity->price. All of
   *    the permutations of format, paper type, and quantity available for
   *    a product should be keys at the appropriate levels in this map.
   */
  initialize: function() {
    M.required(this.get('priceMap'));
    M.required(this.get('urlParams'));
    M.required(this.get('productSku'));
    M.required(this.get('formatOptionList'));

    if (!this.get('isAccessory')) {
      M.required(this.get('envelopeOptionList'));
      M.required(this.get('envelopePriceList'));
      M.required(this.get('bindingOptionList'));
    };

    var urlParams = this.get('urlParams');

    // Build up a list of the paper options from the price dictionary.
    var priceMap = this.get('priceMap');
    var paperOptionList = [];
    _.each(this.get('formatOptionList'), function(formatId) {
      _.each(priceMap[formatId], function(prices, paperId) {
        paperOptionList.push(paperId);
      });
    });

    // Set the default format to the first item in the format options list that
    // is available for the product.
    var defaultFormat = _.find(M.Model.ProductDetails.FORMAT_OPTIONS,
      function(format) {
        return format in priceMap;
      }
    );
    this.set({'defaultFormat': defaultFormat});

    // Set the default paper type to the first item in the paper option list
    // that is available for this product.

    if (!priceMap[defaultFormat]) {
      throw 'Invalid format ' + defaultFormat + ', priceMap=' + JSON.stringify(priceMap);
    }

    var defaultPaper = _.find(M.Model.ProductDetails.PAPER_OPTIONS,
      function(paper) {
        return paper in priceMap[defaultFormat];
      }
    );
    this.set({'defaultPaper': defaultPaper});

    if (this.get('defaultQuantity') == 0 && !this.get('isAccessory')) {
      var defaultQuantity = 10000;
      _.each(priceMap[defaultFormat][defaultPaper], function(price, quantity) {
        if (parseInt(quantity) < defaultQuantity) {
          defaultQuantity = parseInt(quantity);
        }
      });
      this.set({'defaultQuantity': defaultQuantity});
    }

    // Sets the initial values of the available options to the default specified
    // by the product data.
    var format = urlParams['selected_format_id'];
    if (!priceMap.hasOwnProperty(format)) {
      format = defaultFormat;
    }

    this.set({
      'quantity': urlParams['selected_quantity'] || this.get('defaultQuantity'),
      'format': format,
      'paperKindId': urlParams['selected_paperOption'] || defaultPaper
    });

    this.set({'paperOptionList': _.uniq(paperOptionList)});

    if (this.get('envelopeOptionList') && Array.isArray(this.get('envelopeOptionList')) && this.get('envelopeOptionList').length > 0) {
      var defaultEnvelopeOption = this.get('envelopeOptionList').at(0);
      if (defaultEnvelopeOption.color_name && M.ENVELOPE_COLOR_OPTION_NAME_TO_IDX[defaultEnvelopeOption.color_name]) {
        this.set({'selectedEnvelopeColorOptionId': M.ENVELOPE_COLOR_OPTION_NAME_TO_IDX[defaultEnvelopeOption.color_name]});
      }
    }

    if (!this.get('selectedEnvelopeColorOptionId')) {
      this.set({'selectedEnvelopeColorOptionId': EnvelopeColorOption.WHITE});
    }

    this.set({'freeWhiteEnvelopeColorOptionIds': M.FREE_WHITE_ENVELOPE_COLOR_OPTION_IDS});

    // Bind an attribute change to the 'priceUpdated' event. This lets the views
    // know that they need to be refreshed.
    var that = this;
    this.bind('change', function() {
      that.trigger('priceUpdated');
    });
  },

  /**
   * Returns the product kind for this product.
   */
  getProductKind: function() {
    return this.get('productSku').substr(-3);
  },

  isAccessory: function() {
    return this.get('isAccessory');
  },

  /**
   * Returns true if the given combination of format, paper, and quantity is
   * available for this product.
   *
   * A quantity of 0 is tolerated for accessory items only.
   */
  isCombinationValid: function(format, paper, quantity) {
    return format in this.get('priceMap') &&
      paper in this.get('priceMap')[format] &&
      // Quantity is valid if it is an option for the product in the price map
      // OR if the product is an accessory and the quantity has been set to 0.
      (quantity in this.get('priceMap')[format][paper] ||
      (quantity == 0 && this.isAccessory()));
  },

  /**
   * Returns the total price for a given combination of product options.
   *
   * Uses the price map for this product to determine the pricing for a
   * combination of format, paper type, and quantity for this product.
   *
   * Arguments
   * @param {int} format Format id.
   * @param {int} paperType Paper type id.
   * @param {int} quantity Quantity to query for.
   */
  computePriceFor: function(format, paper, quantity) {
    // Accessories can have a quantity of 0, so return 0 for the price. This
    // special case is required because the quantity is not in the price map for
    // this product.
    if (quantity == 0 && this.isAccessory()) {
      return 0;
    }

    var quantityToPriceMap = this.get('priceMap')[format][paper];
    if (!quantityToPriceMap) {
      return 0;
    }

    M.assert(quantityToPriceMap.hasOwnProperty(quantity),
             "quantity '" + quantity +
             "' not found in price map " + JSON.stringify(quantityToPriceMap))
    return quantityToPriceMap[quantity];
  },

  /**
   * Returns the total price for the currently selected product options.
   */
  getCurrentOptionsTotal: function() {
    var format = this.get('format');
    var paperType = this.get('paperKindId');
    var qty = this.get('quantity');
    return this.computePriceFor(format, paperType, qty);
  },

  /**
   * Returns a mapping of quantity -> price for the currently selected
   * format and paper options.
   */
  getQuantityPriceDiffDict: function() {
    var priceMap = this.get('priceMap');
    return priceMap[this.get('format')][this.get('paperKindId')];
  },

  /**
   * Returns the minimum quantity for the current selected format and paper
   * options
   */
  getMinimumQuantity: function(){
    var prices = this.getQuantityPriceDiffDict(),
      lowKey = false;
    for(var i in prices){
      if(prices.hasOwnProperty(i)){
        if(lowKey === false || parseInt(i, 10) < lowKey){
          lowKey = i;
        }
      }
    }
    if(lowKey === false){
      throw "No Valid Integer Keys!";
    }
    return lowKey;
  },

  /**
   * Returns a dictionary mapping the available formats for this product
   * to the additional cost for the format.
   *
   * First computes the total price for the currently selected options
   * and the default format. Then this price is compared to the price
   * for each format to determine how much extra the format would cost.
   */
  getFormatPriceDiffDict: function() {
    var that = this;
    var priceMap = this.get('priceMap');
    var paperType = this.get('paperKindId');
    var qty = this.get('quantity');

    // The baseTotal is the total price for the currently selected paper type
    // and quantity with the default format selected.
    var baseTotal = this.computePriceFor(
      this.get('defaultFormat'),
      paperType,
      qty
    );

    var priceDiffDict = {};

    // Compare the price for the product with each format, holding paper type
    // and quantity constant, to the computed baseTotal. Store the delta in the
    // priceDiffDict.
    _.each(this.get('formatOptionList'), function(formatOption, val) {
      var formatId = formatOption;
      if (that.isCombinationValid(formatId, paperType, qty)) {
        var formatPrice = that.computePriceFor(formatId, paperType, qty);
        priceDiffDict[formatId] = formatPrice - baseTotal;
      } else {
        priceDiffDict[formatId] = null;
      }
    });

    return priceDiffDict;
  },

  getBindingPriceDiff: function() {
    var priceDiff = {};
    _(this.get('bindingOptionList')).each(function(bindingId) {
      priceDiff[bindingId] = 0;
    });

    return priceDiff;
  },

  getDisabledBindings: function() {
    if (_([M.FormatOptions.MONTHLY_PLANNER_NOTEBOOK,
           M.FormatOptions.WEEKLY_PLANNER_NOTEBOOK,
           M.FormatOptions.ADDRESS_BOOK_NOTEBOOK]).contains(this.get('format'))) {
      return [M.BindingOptions.GROMMETED]
    } else {
      return [];
    }
  },

  /**
   * Iterates over every format/priceDiff pair available for the product and
   * applies the given function to that pair.
   *
   * @param {function()} fn A function that takes formatId and priceDiff as
   *  parameters.
   */
  eachFormatPricePair: function(fn) {
    var formatPriceDict = this.getFormatPriceDiffDict();
    _.each(M.Model.ProductDetails.FORMAT_OPTIONS, function(formatId) {
      if (formatId in formatPriceDict) {
        var priceDiff = formatPriceDict[formatId];
        fn(formatId, priceDiff);
      }
    });
  },

  /**
   * Returns a dictionary mapping the available paper types for this
   * product to the additional cost required for that option.
   *
   * The reference point used is the price for the default paper type.
   * All other paper options, keeping the format and quantity constant,
   * are compared against this price.
   */
  getPaperTypePriceDiffDict: function() {
    var that = this;
    var priceMap = this.get('priceMap');
    var format = this.get('format');
    var qty = this.get('quantity');
    var baseTotal = 0;
    if (that.isCombinationValid(format, this.get('defaultPaper'), qty)) {
        baseTotal = this.computePriceFor(
            format,
            this.get('defaultPaper'),
            qty
        );
    }
    var priceDiffDict = {};

    // Keep the format and quantity fixed, then iterate through each paper type
    // looking up the price for that combination. Store the price delta in the
    // priceDiffDict.
    _.each(this.get('paperOptionList'), function(paperOption, val) {
      var paperKindId = paperOption;
      if (that.isCombinationValid(format, paperKindId, qty)) {
        paperPrice = that.computePriceFor(format, paperKindId, qty);
        priceDiffDict[paperKindId] = paperPrice - baseTotal;
      } else {
        priceDiffDict[paperKindId] = null;
      }
    });
    return priceDiffDict;
  },

  /**
   * Iterates over the paper/price diff pairs and yields each one to a given
   * function.
   *
   * @param {function()} fn A function that takes paperKindId and priceDiff as
   *  parameters. This function will be called on every paper/priceDiff
   *  available for this product.
   */
  eachPaperPricePair: function(fn) {
    var paperPriceDict = this.getPaperTypePriceDiffDict();
    _.each(M.Model.ProductDetails.PAPER_OPTIONS, function (paperKindId) {
      if (paperKindId in paperPriceDict) {
        var priceDiff = paperPriceDict[paperKindId];
        fn(paperKindId, priceDiff);
      }
    });
  },

  /**
   * Sets the paper type of the model if it is available with the the other
   * selected options.
   */
  trySetPaper: function(targetPaperType) {
    var formatType = this.get('format');
    if (this.isCombinationValid(formatType, targetPaperType,
      this.get('quantity'))) {
      this.set({'paperKindId': targetPaperType});
    } else if (this.isAccessory()) {
      // If an invalid option is attempted for an accessory, set the paper kind
      // to the default.
      this.set({'paperKindId': this.get('defaultPaper')});
    } else {
      // Tell the views to refresh to return to the original state, since the
      // radio input button would still be selected otherwise.
      this.trigger('priceUpdated');
      var paperOptionChanged = true;
      this.triggerInvalidOptionAttempt(formatType, targetPaperType, paperOptionChanged);
    }
  },

  /**
   * Sets the format of the model, if it is available with the other selected
   * options.
   */
  trySetFormat: function(targetFormat) {
    if (_([M.FormatOptions.MONTHLY_PLANNER_NOTEBOOK,
           M.FormatOptions.WEEKLY_PLANNER_NOTEBOOK,
           M.FormatOptions.ADDRESS_BOOK_NOTEBOOK]).contains(targetFormat) &&
        this.get('bindingId') == M.BindingOptions.GROMMETED
       ) {
      window.parent.M.ProductDetails.openChangeToGrommetNotebook(
        M.Model.ProductDetails.FORMAT_OPTION_LABELS[targetFormat]
      );
      this.set({'format': targetFormat});
    } else if (this.isCombinationValid(targetFormat, this.get('paperKindId'),
      this.get('quantity'))) {
      this.set({'format': targetFormat});
    } else if (this.isAccessory()) {
      // If an invalid option is attempted for an accessory, set the format to
      // the default.
      this.set({'format': this.get('defaultFormat')});
    } else {
      // Tell the views to refresh to return toe the original state, since the
      // radio input button would still be selected otherwise.
      this.trigger('priceUpdated');
      var paperOptionChanged = false;
      this.triggerInvalidOptionAttempt(targetFormat, this.get('paperKindId'), paperOptionChanged);
    }
  },

  /**
   * Sets the format and paper of the model, skipping verification checks
   * Workaround for products whose formats all offer a unique set of paper types
   */
  forceSetFormatAndPaper: function(targetFormat, targetPaperType, defaultFormatType, defaultPaperType) {
    this.set({'format': targetFormat,
              'defaultFormat': defaultFormatType,
              'paperKindId': targetPaperType,
              'defaultPaper': defaultPaperType
            });
  },

  /**
   * Builds and sends intent to product_options_panel.js to display warning dialog
   * on invalid paper/format selection
   */
  triggerInvalidOptionAttempt: function(targetFormatType, targetPaperType, paperOptionChanged) {
    var priceMap = this.get('priceMap');
    var availablePaperFormatPairs = {};
    var paperIdSet = [];
    var paperSetsAreExclusive = true;

    _.each(priceMap, function(paperTypes, formatId) {
      _.each(paperTypes, function(pricings, paperId) {
        if (paperId in paperIdSet) {
          paperSetsAreExclusive = false;
        } else {
          paperIdSet.push(paperId);
        }
        if (!(formatId in availablePaperFormatPairs)) {
          availablePaperFormatPairs[formatId] = [];
        }
        availablePaperFormatPairs[formatId].push(paperId);
      });
    });
    this.trigger('invalidOptionAttempted', targetFormatType, targetPaperType, paperOptionChanged, paperSetsAreExclusive, availablePaperFormatPairs);
  },

  setEnvelopeColor: function(colorId) {
    this.set({'selectedEnvelopeColorOptionId': colorId});
  },

  /*
   * Return a price of zero if no premium envelope color is selected.
   * Otherwise, return a pre-calculated price for the selected color
   */
  getEnvelopePriceForSelectedQuantityAndColorId: function(quantity) {
    var selectedColorOptionId = this.get("selectedEnvelopeColorOptionId");
    var freeWhiteEnvelopeIds = this.get("freeWhiteEnvelopeColorOptionIds");

    if (freeWhiteEnvelopeIds.includes(selectedColorOptionId)) {
      return 0;
    }
    return this.getEnvelopePriceForSelectedQuantity(quantity);
  },

  /*
   * Return a price for the given quantity of envelopes selected.
   * quantity - optional argument; if not supplied, uses the current selected
   * quantity
   */
  getEnvelopePriceForSelectedQuantity: function(quantity) {
    if (!quantity) {
      var quantity = this.get('quantity');
    }
    return this.getEnvelopePrice(quantity);
  },

  getEnvelopePrice: function(quantity) {
    var price = this.get("envelopePriceList")[quantity];

    // envelope_colors_pricing = {} for some SKU's/PK's.
    if (isNaN(price)){
      return 0;
    }

    return price;
  },

  getDieCutPrice: function() {
    var quantity = this.get('quantity');
    var componentCost = this.get('dieCutComponentCost');
    if (this.get('dieCutCode') >= 'A') {
      return componentCost.fixed_cost + quantity * componentCost.piece_cost;
    }
    return 0;
  },

  getDieCutName: function() {
    var name = this.get('dieCutName');
    if (name == '' || name == null)
      return 'Default';
    return this.get('dieCutName');
  }

});
